import { config } from "../../config";

/**
 * The score/status pieces shared by the directory artwork card and the
 * directory-edit table.
 *
 * They lived twice: as JSX inside `DirectoryArtworkCard.tsx` and as
 * `buildScoreBoxes()` / an inline `statusText` chain inside `directoryUpload.ts`,
 * left over from a half-finished conversion. `buildScoreBoxes` was byte-identical
 * to `ScoreBoxes`, but the two status implementations had **drifted** — see the
 * note on `StatusDisplay`.
 */

/** The ten-box optimisation-score meter. */
export function ScoreBoxes({ score }: { score: number }) {
  const boxes = [];
  for (let i = 1; i <= 10; i++) {
    boxes.push(
      <span
        key={i}
        class={`view-data-scorebox2${score >= i ? " view-data-scorebox2-filled" : ""}`}
      />
    );
  }
  return <div class="directory-table-scoreboxes">{boxes}</div>;
}

/**
 * The coloured for-sale status dot and its label.
 *
 * `needsOptIn` is where the two old copies had drifted: the card's JSX showed a
 * red "Expanded Licensing" for an otherwise-sellable artwork awaiting opt-in,
 * while the directory-edit table's string version had no such branch and showed
 * the green "For sale, N MP" — the same artwork reporting two different statuses
 * depending on which panel you looked at.
 *
 * Both call sites now pass a real value: `artwork.directoryRefresh` and
 * `artwork.directoryEditRefresh` compute it identically
 * (`expanded_products === SELECTED && !is_approved_for_expanded_product`).
 */
export function StatusDisplay({
  stage,
  hasCategory,
  megapixels,
  needsOptIn,
}: {
  stage: string;
  hasCategory: boolean;
  megapixels: number | null;
  needsOptIn: boolean;
}) {
  if (config.hiddenSale) {
    return (
      <>
        <span class="directory-dot-red" /> Not for sale
      </>
    );
  }
  if (stage === "READY_FOR_SALE") {
    if (hasCategory) {
      if (megapixels !== null && megapixels > 8) {
        if (needsOptIn) {
          return (
            <>
              <span class="directory-dot-red" /> Expanded Licensing
            </>
          );
        }
        return (
          <>
            <span class="directory-dot-green" /> For sale, {megapixels} MP
          </>
        );
      }
      return (
        <>
          <span class="directory-dot-red" /> Low resolution
        </>
      );
    }
    return (
      <>
        <span class="directory-dot-red" /> Data missing
      </>
    );
  }
  return (
    <>
      <span class="directory-dot-red" /> Not for sale
    </>
  );
}
